import {colors} from '../../../shared/colors'
import {isNonEmpty} from '../../../shared/utils'

export const defaultBreakPoints = {
    label: {
        xs: 12,
        sm: 6,
        md: 2,
        lg: 1
    },
    input: {
        xs: 12,
        sm: 6,
        md: 4,
        lg: 3
    }
}

export const defaultLabelSuffix = ':'

export const defaultFieldLabelStyle = {
    paddingTop: '1rem'
}

const validations = {
    productId: {
        fn: value => /^(?=[0-9]*$)(?:.{9}|.{11})$/.test(value),
        message: `Product ID(NDC) must be 9 or 11 digits.`
    },
    ingredientQty: {
        fn: value => /[A-Za-z]/.test(value) === false && parseFloat(value) > 0,
        message: `Ingredient Quantity must be a positive number`
    }
}

export const validationsMethods = {
    compounds: {
        isValid: compounds => {
            let hasErrors = false;
            const errors = [];
            const dups = { productId: {}, ingredientQty: {}};
            if (compounds && compounds.length) {
                compounds.forEach((compound, index) => {
                    const row = {};
                    ['productId', 'ingredientQty'].forEach(field => {
                        let message = '';
                        let isValid = validations[field].fn(compound[field]);
                        const isDup = field === 'productId' && dups[field] && dups[field][compound[field]];
                        if (!hasErrors && (!isValid || isDup)) {
                            hasErrors = true;
                        }

                        if(isValid && isDup) {
                            isValid = false;
                            message = 'This field must be unique';
                        } else if (!isValid) {
                            message = validations[field].message;
                        }
                        row[field] = {isValid, message};
                        dups[field][compound[field]] = 1;
                    })
                    errors.push(row);
                })
            }

            return hasErrors ? {errors} : true;
        },
        getErrorMsg: field => `Compounds is errant`
    },
    phone: {
        isValid: value => /^[0-9]{10}$/.test(value),
        getErrorMsg: field => `${field} is not a valid phone number.`
    },
    wholeNumber: {
        isValid: value => /^[1-9][0-9]*$/.test(value),
        getErrorMsg: field => `${field} must be a whole number.`
    },
    ndc: {
        isValid: value => /^(?=[0-9]*$)(?:.{11})$/.test(value),
        getErrorMsg: field => `${field} must be 11 digits.`
    },
    validNDC: {
        isValid: (value, comparedValues) => {
            if (value.length === 11 && comparedValues[0].length > 1) {
                return false;
            } else {
                return true;
            }
        },
        getErrorMsg: field => `Invalid ${field}, Please enter a valid NDC.`
    },
    beneficiaryID: {
        isValid: value => /^[a-zA-Z0-9]{9}$/.test(value) 
            && isNonEmpty(value.match(/[a-zA-Z]/g)) 
            && value.match(/[a-zA-Z]/g).length === 1,
        getErrorMsg: field => `${field} must be 9 characters with exactly one alphabet character`
    },
    positiveNumber: {
        isValid: value => /[A-Za-z]/.test(value) === false && parseFloat(value) > 0,
        getErrorMsg: field => `${field} must be a positive number.`
    },
    positiveInteger: {
        isValid: value => Number.isInteger(Number(value)) && parseInt(value) > 0,
        getErrorMsg: field => `${field} must be a positive integer.`
    },
    positiveIntegerZero: {
        isValid: value => Number.isInteger(Number(value)) && parseInt(value) > -1,
        getErrorMsg: field => `${field} must be a 0 or higher.`
    },
    required: {
        isValid: value => isNonEmpty(value),
        getErrorMsg: field => `${field} is required.`
    },
    dateFmt: {
        isValid: value => {
            const format = window.moment(value).format('MM/DD/YYYY')
            const hasDateFmt = typeof format === 'string' && format !== 'Invalid date'
            return value !== '' && value !== null ? hasDateFmt : true
        },
        getErrorMsg: field => `${field} has format MM/DD/YYYY.`
    },
    dateNotInFuture: {
        isValid: value => {
            const format = window.moment(value).format('MM/DD/YYYY')
            const hasDateFmt = typeof format === 'string' && format !== 'Invalid date'
            const delta = window.moment(value, 'MM/DD/YYYY').fromNow()
            const isNotInFuture = delta.includes('ago')
            return hasDateFmt ? isNotInFuture : true
        },
        getErrorMsg: field => `${field} cannot occur in future.`
    },
    drugNotFailed: {
        isValid: (value) => {  return true; },
        getErrorMsg: (field) => {
            return 'This drug has failed trial authorization.'
        }
    },
    dateGreaterThan: {
        isValid: (value, comparedValues) => {
            const format = window.moment(value).format('MM/DD/YYYY')
            const hasDateFmt = typeof format === 'string' && format !== 'Invalid date'
            const vals = comparedValues.map(val => {
                return isNonEmpty(val) ? window.moment(value, 'MM/DD/YYYY').isAfter(val) : true
            })
            const isAfter = vals.every(val => val === true)
            return hasDateFmt ? isAfter : true
        },
        getErrorMsg: (field, comparedFields) => comparedFields.map(cf => `${field} must occur after ${cf}.`).join('')
    },
    dateLessThan: {
        isValid: (value, comparedValues) => {
            const format = window.moment(value).format('MM/DD/YYYY')
            const hasDateFmt = typeof format === 'string' && format !== 'Invalid date'
            const vals = comparedValues.map(val => {
                return isNonEmpty(val) ? window.moment(value).isBefore(val) : true
            })
            const isBefore = vals.every(val => val === true)
            return hasDateFmt ? isBefore : true
        },
        getErrorMsg: (field, comparedFields) => comparedFields.map(cf => `${field} must occur before ${cf}.`).join('')
    }
}

export const defaultLabelViewportRatio = 1.6

export const defaultTextAreaViewportRatio = 13

export const defaultTextAreaRowSize = 4

export const defaultTextAreaCharCount = 4000

export const defaultInputStyle = {width: '25ch'}

export const defaultHelperTextStyle = {width: '30ch', color: '#a32d24'}

export const getDefaultSummaryFieldConfig = (fieldConfig) => {

    const summary = fieldConfig.summary ? fieldConfig.summary : {}

    return {
        type: 'prefilled',
        fieldLabelStyle: {
            paddingTop: '0',
            ...fieldConfig.fieldLabelStyle,
            ...summary.fieldLabelStyle
        },
        fieldInputStyle: {
            ...fieldConfig.fieldInputStyle,
            ...summary.fieldInputStyle
        },
        ...fieldConfig.summary,
    }
}

export const getDefaultFieldLabelTextStyle = (isLastStep) => ({
    color: isLastStep ? colors.magellanDarkBlue : null,
    fontWeight: isLastStep ? 'bold' : null
})